// src/features/analytics/AnalyticsPage.tsx
import { Card, EmptyState, formatPersianNumber } from '../../app/components/foundation';
import { ControlPlaneMetricStrip, ControlPlanePage } from '../../app/components/foundation/control-plane';
import { VaniaContentSkeleton, VaniaLoadingSystem } from '../../app/components/loading';
import { AnalyticsLiveOverview } from './components';
import { useAnalytics } from './hooks/useAnalytics';
import './styles/analytics.css';

/**
 * Analytics overview presentation. Data access and calculations remain
 * owned by useAnalytics; this component only arranges the live snapshot
 * inside the shared Control Plane surfaces.
 */
export function AnalyticsPage() {
  const { analytics, loading, error } = useAnalytics();
  const initialLoading = loading && !analytics;

  return (
    <ControlPlanePage
      className="vania-analytics-control-plane-page"
      title=""
      actions={<button type="button" disabled className="vania-analytics-action-placeholder" title="در فاز بعدی فعال می‌شود">خروجی گزارش</button>}
      summary={analytics ? (
        <ControlPlaneMetricStrip
          className="vania-analytics-summary-strip"
          items={[
            { label: 'کل کاربران', value: formatPersianNumber(analytics.overview.total_users) },
            { label: 'کاربران فعال', value: formatPersianNumber(analytics.overview.active_users) },
            { label: 'درآمد', value: `${formatPersianNumber(analytics.overview.revenue)} تومان`, tone: 'success' },
            { label: 'تعداد سفارش‌ها', value: formatPersianNumber(analytics.orders.count) },
            { label: 'حجم تراکنش‌ها', value: `${formatPersianNumber(analytics.wallet.transaction_volume)} تومان` },
            { label: 'کمیسیون کل', value: `${formatPersianNumber(analytics.commission.total)} تومان` },
            { label: 'کمیسیون پرداخت‌شده', value: `${formatPersianNumber(analytics.commission.paid)} تومان`, tone: 'success' },
            { label: 'کمیسیون در انتظار', value: `${formatPersianNumber(analytics.commission.pending)} تومان`, tone: 'warning' },
            { label: 'کاربران جدید', value: formatPersianNumber(analytics.overview.new_users) },
          ]}
        />
      ) : undefined}
    >
      {error && !loading ? <p className="vania-async-state vania-async-state-error" role="alert">{error}</p> : null}
      {initialLoading ? <AnalyticsLoadingSkeleton /> : null}
      {!loading && !error && !analytics ? <EmptyState message="اطلاعات تحلیلی برای نمایش وجود ندارد." /> : null}
      {analytics ? <AnalyticsLiveOverview analytics={analytics} /> : null}
    </ControlPlanePage>
  );
}

function AnalyticsLoadingSkeleton() {
  return (
    <VaniaLoadingSystem mode="content" className="vania-analytics-loading-system" label="در حال بارگذاری اطلاعات تحلیلی">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3" aria-hidden="true">
        {Array.from({ length: 9 }, (_, index) => (
          <Card key={`analytics-metric-skeleton-${index}`}>
            <VaniaContentSkeleton style={{ width: '42%', height: '0.75rem' }} />
            <VaniaContentSkeleton style={{ width: '64%', height: '1.2rem' }} />
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2" aria-hidden="true">
        {Array.from({ length: 2 }, (_, index) => (
          <Card key={`analytics-summary-skeleton-${index}`}>
            <VaniaContentSkeleton style={{ width: '34%', height: '1rem' }} />
            <VaniaContentSkeleton style={{ width: '72%', height: '0.85rem' }} />
            <VaniaContentSkeleton style={{ width: '62%', height: '0.85rem' }} />
            <VaniaContentSkeleton style={{ width: '54%', height: '0.85rem' }} />
          </Card>
        ))}
      </div>
      <AnalyticsTableSkeleton columns={3} rows={6} label="روند زمانی" />
      <AnalyticsTableSkeleton columns={4} rows={6} label="عملکرد نمایندگان" />
    </VaniaLoadingSystem>
  );
}

function AnalyticsTableSkeleton({ columns, rows, label }: { columns: number; rows: number; label: string }) {
  return (
    <Card aria-hidden="true">
      <VaniaContentSkeleton style={{ width: '30%', height: '1rem' }} />
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              {Array.from({ length: columns }, (_, index) => <th key={`${label}-heading-${index}`}><VaniaContentSkeleton style={{ width: '70%', height: '0.75rem' }} /></th>)}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }, (_, rowIndex) => (
              <tr key={`${label}-row-${rowIndex}`}>
                {Array.from({ length: columns }, (_, cellIndex) => <td key={`${label}-cell-${rowIndex}-${cellIndex}`}><VaniaContentSkeleton style={{ width: cellIndex === 0 ? '70%' : '55%' }} /></td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
